import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const InterviewContext = createContext();

export const useInterview = () => {
    const context = useContext(InterviewContext);
    if (!context) {
        throw new Error('useInterview must be used within an InterviewProvider');
    }
    return context;
};

export const InterviewProvider = ({ children }) => {
    // Persistent User Context (Target Role, Company, etc.)
    const [userContext, setUserContext] = useState(() => {
        const saved = localStorage.getItem('interview_user_context');
        return saved ? JSON.parse(saved) : {
            role: "",
            company: "",
            initiative: "",
            jobDescription: ""
        };
    });

    // Voice Settings
    const [voiceSettings, setVoiceSettings] = useState(() => {
        const defaults = {
            voiceUri: '',
            rate: 1,
            pitch: 1,
            useNeuralVoice: true, // Default to high-fidelity
            elevenLabsKey: process.env.REACT_APP_ELEVENLABS_API_KEY || '',
            elevenLabsVoiceId: process.env.REACT_APP_ELEVENLABS_VOICE_ID || 'Lcf7NAHS-fVbv7TXYjMv', // Emily (Lady) - Sophisticated & Clear
            premiumVoices: [
                { name: "Thomas (Professional)", id: "GBv7mTt0atIp3o8V9jmL" },
                { name: "Emily (Lady)", id: "Lcf7NAHS-fVbv7TXYjMv" },
                { name: "Adam (Deep Male)", id: "pNInz6obpgDQGcFmaJgB" },
                { name: "Josh (Young Male)", id: "TxGEqnHW47iw3t83tnJ7" },
                { name: "Rachel (Neutral Female)", id: "21m00Tcm4lfs7710RTZZ" }
            ]
        };
        const saved = localStorage.getItem('interview_voice_settings');
        return saved ? { ...defaults, ...JSON.parse(saved) } : defaults; // Merge defaults with saved
    });

    // Persistent Coach Mode (Interview vs Learn vs Salesman)
    const [coachMode, setCoachMode] = useState(() => {
        const saved = localStorage.getItem('interview_coach_mode');
        // If it was 'salesman', fallback to 'interview' for persistence, 
        // as 'salesman' is usually triggered by page context.
        return (saved && saved !== 'salesman') ? saved : 'interview';
    });

    // Persistent Chat History
    const [chatHistory, setChatHistory] = useState(() => {
        const saved = localStorage.getItem('interview_chat_history');
        return saved ? JSON.parse(saved) : [];
    });

    // Current Topic context (changes based on what user is looking at)
    const [activeTopic, setActiveTopic] = useState(null);
    const [isCoachOpen, setIsCoachOpen] = useState(false);
    const currentAudioRef = useRef(null);

    // Persist userContext changes
    useEffect(() => {
        localStorage.setItem('interview_user_context', JSON.stringify(userContext));
    }, [userContext]);

    // Persist coachMode changes
    useEffect(() => {
        localStorage.setItem('interview_coach_mode', coachMode);
    }, [coachMode]);

    // Voice Mode toggle (Global) - Always start OFF by default to prevent unwanted auto-play
    const [isVoiceMode, setIsVoiceMode] = useState(false);

    const [currentSpeakingId, setCurrentSpeakingId] = useState(null);

    const speakWithSystem = (text, id, settings = voiceSettings) => {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setCurrentSpeakingId(id || Date.now());
        utterance.onend = () => setCurrentSpeakingId(null);
        utterance.onerror = (e) => {
            console.error('Speech Synthesis Error:', e);
            setCurrentSpeakingId(null);
        };

        utterance.rate = settings.rate || 1;
        utterance.pitch = settings.pitch || 1;
        utterance.volume = 1;

        const setVoice = () => {
            window.speechSynthesis.resume();
            const voices = window.speechSynthesis.getVoices();
            let selectedVoice = voices.find(v => v.voiceURI === settings.voiceUri);
            if (!selectedVoice) {
                selectedVoice = voices.find(v => (v.name.includes('Google') || v.name.includes('Neural') || v.name.includes('Premium')) && v.lang.startsWith('en'));
                if (!selectedVoice) selectedVoice = voices.find(v => v.lang.startsWith('en'));
            }
            if (selectedVoice) utterance.voice = selectedVoice;
            window.speechSynthesis.speak(utterance);
        };

        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = setVoice;
        } else {
            setVoice();
        }
    };

    const speakWithElevenLabs = async (text, id, settings = voiceSettings) => {
        if (!settings.elevenLabsKey) return;

        try {
            console.info("ElevenLabs: Sending TTS request...");
            const voiceId = settings.elevenLabsVoiceId || "Lcf7NAHS-fVbv7TXYjMv";
            const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'xi-api-key': settings.elevenLabsKey
                },
                body: JSON.stringify({
                    text,
                    model_id: "eleven_multilingual_v2",
                    voice_settings: { stability: 0.6, similarity_boost: 0.75 }
                })
            });

            if (response.ok) {
                console.info("ElevenLabs: Audio received successfully.");
                const audioBlob = await response.blob();
                const audioUrl = URL.createObjectURL(audioBlob);
                const audio = new Audio(audioUrl);
                currentAudioRef.current = audio;
                audio.volume = 1;
                audio.onplay = () => setCurrentSpeakingId(id || Date.now());
                audio.onended = () => {
                    setCurrentSpeakingId(null);
                    URL.revokeObjectURL(audioUrl);
                };
                audio.onerror = (e) => {
                    console.error('ElevenLabs Audio Error:', e);
                    speakWithSystem(text, id, settings);
                };
                audio.play().catch(err => {
                    console.warn('ElevenLabs play blocked:', err);
                    speakWithSystem(text, id, settings);
                });
            } else {
                console.warn('ElevenLabs API Error:', response.status);
                speakWithSystem(text, id, settings);
            }
        } catch (error) {
            console.error('ElevenLabs Network Error:', error);
            speakWithSystem(text, id, settings);
        }
    };

    const cleanTextForSpeech = (text) => {
        if (!text) return "";
        return text
            .replace(/\*\*(.*?)\*\*/g, '$1') // Bold **
            .replace(/\*(.*?)\*/g, '$1')     // Italic *
            .replace(/__(.*?)__/g, '$1')    // Bold __
            .replace(/_(.*?)_/g, '$1')      // Italic _
            .replace(/#{1,6}\s+(.*)/g, '$1') // Headers #
            .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Links [text](url) -> text
            .replace(/`{1,3}.*?`{1,3}/gs, '') // Code blocks (remove entirely for speech)
            .replace(/^>\s+/gm, '')          // Blockquotes >
            .replace(/^[\s-*+·•]+\s+/gm, '') // Bullet points
            .replace(/^\s*\d+[.)]\s+/gm, '') // Numbered lists
            .replace(/[*_#~`<>|]/g, '')     // Remaining special chars
            .replace(/\s+/g, ' ')           // Flatten whitespace
            .trim();
    };

    const speakText = (text, id, settings = voiceSettings) => {
        if (!isVoiceMode && !id) return; // Allow forcing speak via ID (for tests)

        const cleanedText = cleanTextForSpeech(text);
        if (!cleanedText) return;

        if (settings.useNeuralVoice && settings.elevenLabsKey) {
            speakWithElevenLabs(cleanedText, id, settings);
        } else {
            speakWithSystem(cleanedText, id, settings);
        }
    };

    const stopSpeaking = () => {
        window.speechSynthesis.cancel();
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
        }
        setCurrentSpeakingId(null);
    };

    // Persist voiceSettings changes
    useEffect(() => {
        localStorage.setItem('interview_voice_settings', JSON.stringify(voiceSettings));
    }, [voiceSettings]);



    // Persist chatHistory changes
    useEffect(() => {
        localStorage.setItem('interview_chat_history', JSON.stringify(chatHistory));
    }, [chatHistory]);

    const updateUserContext = (newContext) => {
        setUserContext(prev => ({ ...prev, ...newContext }));
    };

    const updateVoiceSettings = (newSettings) => {
        setVoiceSettings(prev => ({ ...prev, ...newSettings }));
    };

    const clearHistory = () => {
        setChatHistory([]);
        localStorage.removeItem('interview_chat_history');
    };

    const toggleCoach = (isOpen) => {
        setIsCoachOpen(isOpen !== undefined ? isOpen : !isCoachOpen);
    };

    const value = {
        userContext,
        updateUserContext,
        chatHistory,
        setChatHistory,
        clearHistory,
        activeTopic,
        setActiveTopic,
        isCoachOpen,
        toggleCoach,
        coachMode,
        setCoachMode,
        voiceSettings,
        updateVoiceSettings,
        isVoiceMode,
        setIsVoiceMode,
        currentSpeakingId,
        speakText,
        stopSpeaking
    };

    return (
        <InterviewContext.Provider value={value}>
            {children}
        </InterviewContext.Provider>
    );
};
